import React, { useState, useEffect } from "react"
import { ref, onValue } from "firebase/database"
import { Button, Snackbar } from "@mui/material"

import { sdk9db } from "../firebase"

declare global {
  interface Window {
    APP_VERSION: string
  }
}

interface Version {
  major: number
  minor: number
  patch: number
}

function getVersionDetails(version: string | null | undefined): Version | null {
  if (!version) {
    return null
  }

  const [major, minor, patch] = version.split(".")

  return {
    major: parseInt(major, 10),
    minor: parseInt(minor, 10),
    patch: parseInt(patch, 10),
  }
}

const currentVersion = getVersionDetails(window.APP_VERSION)

const AppVersionGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [wantedVersion, setWantedVersion] = useState<Version | null>(null)

  useEffect(() => {
    const appVersionUnsub = onValue(
      ref(sdk9db, "appVersion/latest"),
      (snap) => {
        const version = snap.val()
        // eslint-disable-next-line no-console
        console.log(
          `Wanted version: ${version} - Current version: ${window.APP_VERSION}`,
        )
        setWantedVersion(getVersionDetails(version))
      },
    )

    return () => {
      if (appVersionUnsub) {
        appVersionUnsub()
      }
    }
  }, [])

  if (
    wantedVersion &&
    currentVersion &&
    currentVersion.major !== wantedVersion.major
  ) {
    return (
      <div style={{ maxWidth: 800, margin: "0 auto", padding: 40 }}>
        <h2>We&apos;ve made upgrades!</h2>
        <p>
          Please excuse this interruption. We have just released a new version
          of the Coastline website. Please{" "}
          <a href={window.location.href}>refresh the page to continue</a>.
        </p>
      </div>
    )
  }

  return (
    <>
      {wantedVersion &&
      currentVersion &&
      currentVersion.minor !== wantedVersion.minor ? (
        <Snackbar
          open={true}
          message="There's a new version of the app available. Please refresh the page to update."
          action={
            <Button
              color="secondary"
              size="small"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          }
        />
      ) : null}
      {children}
    </>
  )
}

export default AppVersionGate
